import type { Email, LoadStatus } from "@src/types/api";
import type { LoadApplication } from "@src/load-agent/LoadApplication";
import type { PostBookWorkflowSideEffects } from "@src/load-agent/domain/postBookWorkflowSideEffects";
import { statusHistoryIdentityPayload } from "@src/load-agent/office/resolveActorEmailForThread";

export interface CompleteBookingTaskpaneRequest {
  mode: "LTL" | "FTL";
  shipmentId: string;
  email: Email;
  selectedOfferId?: string;
}
export interface CompleteBookingTaskpaneResult {
  success: boolean;
  loadId?: number;
  bolDocsCount?: number;
  emailDraftCreated?: boolean;
  attachmentDownloadUrls?: {
    name: string;
    url: string;
  }[];
  error?: string;
}

type BookingLoadApp = Pick<LoadApplication, "postLoadAgentBook">;

export async function runCompleteBookingTaskpaneWorkflow(
  request: CompleteBookingTaskpaneRequest,
  loadApp: BookingLoadApp,
  fx: PostBookWorkflowSideEffects
): Promise<CompleteBookingTaskpaneResult> {
  const { mode, shipmentId, email, selectedOfferId } = request;
  const internetMessageId = email.internetMessageId;

  if (!internetMessageId) {
    return { success: false, error: "No internet message ID found" };
  }
  try {
    const bookingResponse = await loadApp.postLoadAgentBook({
      shipmentId,
      internetMessageId,
      selectedOfferId,
    });
    const { loadId, confirmationHtml, replyAttachments } = bookingResponse;
    let emailDraftCreated = false;

    try {
      await fx.replyAllWithAttachments({
        htmlBody: confirmationHtml,
        attachments: replyAttachments,
      });
      emailDraftCreated = true;
    } catch (error) {
      void error;
    }
    const statusLabel = emailDraftCreated ? "BOL Sent" : "Load Won";

    try {
      await fx.updateLoadStatus({
        mode,
        shipmentId,
        internetMessageId,
        loadStatus: statusLabel as LoadStatus,
        ...statusHistoryIdentityPayload(email),
      });
    } catch (error) {
      void error;
    }
    const attachmentDownloadUrls =
      emailDraftCreated && replyAttachments.length > 0
        ? replyAttachments.map((a) => ({ name: a.name, url: a.url }))
        : undefined;

    return {
      success: true,
      loadId,
      bolDocsCount: replyAttachments.length,
      emailDraftCreated,
      attachmentDownloadUrls,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Booking workflow failed",
    };
  }
}
